import { createAsyncThunk } from "@reduxjs/toolkit";

import * as emailTemplateApi from "../services/emailTemplateApi";
import * as emailTemplateCategoryApi from "../services/emailTemplateCategoryApi";
import { getErrorMessage } from "../utils/errorUtils";

import type { EmailTemplateFormPayload } from "./types";

const readActiveCompany = (getState: () => any) => {
  const state: any = getState();
  const fromEmailTemplates = state.emailTemplates?.activeCompany;
  const fromRecruitment = state.recruitment?.activeCompany;
  const fromCompany = state.company?.activeCompany;
  return fromEmailTemplates || fromRecruitment || fromCompany || {};
};

export const fetchEmailTemplates = createAsyncThunk(
  "emailTemplates/fetchAll",
  async (
    args: { category?: string } | undefined,
    { getState, rejectWithValue },
  ) => {
    try {
      const { company_id, company_type } = readActiveCompany(getState);
      return await emailTemplateApi.listEmailTemplates(
        company_id,
        company_type,
        args?.category,
      );
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error));
    }
  },
);

export const createEmailTemplate = createAsyncThunk(
  "emailTemplates/create",
  async (template: EmailTemplateFormPayload, { getState, rejectWithValue }) => {
    try {
      const { company_id, company_type } = readActiveCompany(getState);
      return await emailTemplateApi.createEmailTemplate(
        company_id,
        company_type,
        template,
      );
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error));
    }
  },
);

export const updateEmailTemplate = createAsyncThunk(
  "emailTemplates/update",
  async (
    {
      templateId,
      template,
    }: { templateId: number; template: Partial<EmailTemplateFormPayload> },
    { getState, rejectWithValue },
  ) => {
    try {
      const { company_id, company_type } = readActiveCompany(getState);
      return await emailTemplateApi.updateEmailTemplate(
        company_id,
        company_type,
        templateId,
        template,
      );
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error));
    }
  },
);

export const deleteEmailTemplate = createAsyncThunk(
  "emailTemplates/delete",
  async (templateId: number, { getState, rejectWithValue }) => {
    try {
      const { company_id, company_type } = readActiveCompany(getState);
      await emailTemplateApi.deleteEmailTemplate(
        company_id,
        company_type,
        templateId,
      );
      return templateId;
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error));
    }
  },
);

export const previewEmailTemplate = createAsyncThunk(
  "emailTemplates/preview",
  async (
    {
      templateId,
      sampleData,
    }: { templateId: number; sampleData?: Record<string, any> },
    { getState, rejectWithValue },
  ) => {
    try {
      const { company_id, company_type } = readActiveCompany(getState);
      return await emailTemplateApi.previewEmailTemplate(
        company_id,
        company_type,
        templateId,
        sampleData || {},
      );
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error));
    }
  },
);

export const fetchCategories = createAsyncThunk(
  "emailTemplates/fetchCategories",
  async (_, { getState, rejectWithValue }) => {
    try {
      const { company_id, company_type } = readActiveCompany(getState);
      if (!company_id) return [];
      return await emailTemplateCategoryApi.listCategories(
        company_id,
        company_type,
      );
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error));
    }
  },
);

export const createCategory = createAsyncThunk(
  "emailTemplates/createCategory",
  async (
    payload: { value: string; label: string; icon?: string; color?: string },
    { getState, rejectWithValue },
  ) => {
    try {
      const { company_id, company_type } = readActiveCompany(getState);
      return await emailTemplateCategoryApi.createCategory(
        company_id,
        company_type,
        payload,
      );
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error));
    }
  },
);

export const updateCategory = createAsyncThunk(
  "emailTemplates/updateCategory",
  async (
    {
      categoryId,
      payload,
    }: {
      categoryId: number;
      payload: {
        label?: string;
        icon?: string;
        color?: string;
        is_active?: boolean;
      };
    },
    { getState, rejectWithValue },
  ) => {
    try {
      const { company_id, company_type } = readActiveCompany(getState);
      return await emailTemplateCategoryApi.updateCategory(
        company_id,
        company_type,
        categoryId,
        payload,
      );
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error));
    }
  },
);

export const deleteCategory = createAsyncThunk(
  "emailTemplates/deleteCategory",
  async (categoryId: number, { getState, rejectWithValue }) => {
    try {
      const { company_id, company_type } = readActiveCompany(getState);
      await emailTemplateCategoryApi.deleteCategory(
        company_id,
        company_type,
        categoryId,
      );
      return categoryId;
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error));
    }
  },
);
